import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../utils/baseUrl";
import { config } from "../utils/axiosconfig";
import { Spin, Tag, Avatar, Card, Row, Col, Modal, Form, Input, Button } from "antd";
import { toast } from "react-toastify";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  IdcardOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";

const statCardStyle = (bg) => ({
  background: bg,
  borderRadius: 10,
  color: "#fff",
  padding: "20px 24px",
  marginBottom: 0,
  border: "none",
  boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
});

const sectionCard = {
  borderRadius: 10,
  marginBottom: 24,
  boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
  border: "1px solid #e8e8e8",
};

const labelStyle = {
  color: "#888",
  fontSize: 12,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const valueStyle = {
  color: "#222",
  fontSize: 14,
  fontWeight: 600,
  marginTop: 2,
};

const InfoItem = ({ label, value, icon }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={labelStyle}>
      {icon && <span style={{ marginRight: 5 }}>{icon}</span>}
      {label}
    </div>
    <div style={valueStyle}>{value || "—"}</div>
  </div>
);

const MyProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [form] = Form.useForm();

  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword)
      return toast.error("Passwords do not match");
    if (values.newPassword.length < 8)
      return toast.error("Password must be at least 8 characters");

    Modal.confirm({
      title: "Change Password",
      content: "Are you sure you want to change your password?",
      okText: "Yes, Change",
      cancelText: "Cancel",
      okButtonProps: { style: { background: "#4f46e5", borderColor: "#4f46e5" } },
      onOk: async () => {
        setPwdLoading(true);
        try {
          await axios.put(`${base_url}user/password`, { password: values.newPassword }, config);
          toast.success("Password changed! Logging out...");
          setPwdModal(false);
          form.resetFields();
          setTimeout(() => {
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            localStorage.removeItem("user");
            localStorage.removeItem("email");
            window.location.href = "/";
          }, 1500);
        } catch (err) {
          toast.error(err?.response?.data?.message || "Failed to change password");
        } finally {
          setPwdLoading(false);
        }
      },
    });
  };

  useEffect(() => {
    const user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    if (!user?._id) return;

    axios
      .get(`${base_url}user/myProfile/${user._id}`, config)
      .then((res) => {
        setProfileData(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profileData) {
    return <div className="text-center mt-5">Profile data not found.</div>;
  }

  const { getaUser: u, totalWorkingHours, latestAttendanceRecord, attendanceStatus } = profileData;

  const workingHoursStr = totalWorkingHours
    ? `${totalWorkingHours.hours}h ${totalWorkingHours.minutes}m ${totalWorkingHours.seconds}s`
    : "—";

  return (
    <div style={{ width: "100%" }}>

      {/* Hero Header */}
      <Card
        style={{
          borderRadius: 12,
          marginBottom: 24,
          background: "linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)",
          border: "none",
          boxShadow: "0 4px 16px rgba(15,52,96,0.18)",
        }}
        bodyStyle={{ padding: "32px 40px" }}
      >
        <div className="d-flex align-items-center gap-4 flex-wrap">
          <Avatar
            size={90}
            src={u?.images?.[0]?.url}
            icon={<UserOutlined />}
            style={{ background: "#e8f4fd", color: "#0f3460", fontSize: 38, flexShrink: 0, border: "3px solid rgba(255,255,255,0.2)" }}
          />
          <div style={{ flex: 1 }}>
            <h2 style={{ color: "#fff", margin: 0, fontSize: 26, fontWeight: 700 }}>{u?.name || "—"}</h2>
            <div style={{ color: "#aac4e0", fontSize: 13, marginTop: 4 }}>{u?.email}</div>
            <div className="d-flex gap-2 mt-3 flex-wrap">
              <Tag color="blue" style={{ textTransform: "capitalize", fontWeight: 600 }}>{u?.role}</Tag>
              {u?.isBlocked
                ? <Tag color="red" icon={<CloseCircleOutlined />}>Blocked</Tag>
                : <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>}
              {u?.profile_complete
                ? <Tag color="cyan">Profile Complete</Tag>
                : <Tag color="orange">Profile Incomplete</Tag>}
            </div>
          </div>
          {/* Quick Stats */}
          <div className="d-flex gap-3 flex-wrap">
            <div style={statCardStyle("#27ae60")}>
              <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Attendance</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{attendanceStatus || "—"}</div>
            </div>
            <div style={statCardStyle("#2980b9")}>
              <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Working Hours</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{workingHoursStr}</div>
            </div>
            <div
              onClick={() => setPwdModal(true)}
              style={{ ...statCardStyle("#7c3aed"), cursor: "pointer" }}
            >
              <div style={{ fontSize: 11, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.5 }}>Security</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>Change Password</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Stat Cards Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <div style={statCardStyle("#27ae60")}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Unique ID</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{u?.unique_id || "—"}</div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={statCardStyle("#2980b9")}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Role</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, textTransform: "capitalize" }}>{u?.role || "—"}</div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={statCardStyle("#e67e22")}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Qualification</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{u?.qualification || "—"}</div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div style={statCardStyle("#c0392b")}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Experience</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{u?.experience || "—"}</div>
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 0]}>
        {/* Left Column */}
        <Col xs={24} md={12}>
          {/* Personal Info */}
          <Card title={<span style={{ fontWeight: 700 }}>Personal Information</span>} style={sectionCard}>
            <InfoItem label="Full Name" value={u?.name} icon={<UserOutlined />} />
            <InfoItem label="Email" value={u?.email} icon={<MailOutlined />} />
            <InfoItem label="Phone" value={u?.phone_no} icon={<PhoneOutlined />} />
            <InfoItem label="Skill Area" value={u?.skill_area} />
            <InfoItem label="Job Role" value={u?.job_role} />
            <InfoItem label="Address" value={u?.address} icon={<EnvironmentOutlined />} />
          </Card>

          {/* Account Info */}
          <Card title={<span style={{ fontWeight: 700 }}>Account Information</span>} style={sectionCard}>
            <InfoItem label="User ID" value={u?._id} icon={<IdcardOutlined />} />
            <InfoItem label="VC User ID" value={u?.vcuser_id} />
            <InfoItem
              label="Account Created"
              value={u?.createdAt ? new Date(u.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            />
            <InfoItem
              label="Last Updated"
              value={u?.updatedAt ? new Date(u.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            />
          </Card>
        </Col>

        {/* Right Column */}
        <Col xs={24} md={12}>
          {/* Location Details */}
          <Card title={<span style={{ fontWeight: 700 }}>Location Details</span>} style={sectionCard}>
            <InfoItem label="State" value={u?.state_name || u?.state} icon={<EnvironmentOutlined />} />
            <InfoItem label="District" value={u?.district} />
            <InfoItem label="Cluster" value={u?.cluster_name} />
            <InfoItem label="School" value={u?.school_name} icon={<BankOutlined />} />
            <InfoItem label="School ID" value={u?.schoolId} />
            <InfoItem label="Latitude" value={u?.lat} />
            <InfoItem label="Longitude" value={u?.long} />
          </Card>

          {/* Attendance Summary */}
          <Card title={<span style={{ fontWeight: 700 }}>Attendance Summary</span>} style={sectionCard}>
            <InfoItem label="Status" value={attendanceStatus} icon={<CheckCircleOutlined />} />
            <InfoItem label="Total Working Hours" value={workingHoursStr} icon={<ClockCircleOutlined />} />
            {latestAttendanceRecord && (
              <>
                <InfoItem
                  label="Last Punch In"
                  value={latestAttendanceRecord.punchInTime
                    ? new Date(latestAttendanceRecord.punchInTime).toLocaleString()
                    : "—"}
                />
                <InfoItem
                  label="Last Punch Out"
                  value={latestAttendanceRecord.punchOutTime
                    ? new Date(latestAttendanceRecord.punchOutTime).toLocaleString()
                    : "Not punched out"}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        open={pwdModal}
        onCancel={() => { setPwdModal(false); form.resetFields(); }}
        footer={null}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[{ required: true, message: "Please enter new password" }]}
          >
            <Input.Password placeholder="Enter new password (min 8 chars)" />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[{ required: true, message: "Please confirm your password" }]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button onClick={() => { setPwdModal(false); form.resetFields(); }} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={pwdLoading}
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            >
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MyProfile;
